"use client"

import { Check } from "lucide-react"
import { useTranslation } from "react-i18next"
import { useToast } from "@/shared/hooks/use-toast"
import { useIsMobile } from "@/shared/hooks/use-mobile"
import {
  Toast,
  ToastClose,
  ToastDescription,
  ToastProvider,
  ToastTitle,
  ToastViewport,
} from "@/shared/ui/toast"

export function Toaster() {
  const { toasts, dismiss } = useToast()
  const isMobile = useIsMobile()
  const { t } = useTranslation()

  return (
    <ToastProvider>
      {/* `priority` is pulled out of the spread on purpose: it is a scheduling hint
          the toast STORE reads (see use-toast.ts ADD_TOAST) and means nothing to
          Radix, which forwards whatever it does not recognise straight onto the
          DOM element. Discarding it here is the point, hence the disable. */}
      {/* eslint-disable-next-line @typescript-eslint/no-unused-vars -- destructured to be discarded, see above */}
      {toasts.map(function ({ id, title, description, action, duration, priority, ...props }) {
        // Toasts with action buttons (e.g. Undo) keep a long window.
        // Errors stay prominent at 5s. Plain confirmations vanish in 2s.
        const autoDuration = duration ?? (
          action ? 8000 :
          props.variant === 'destructive' ? 5000 :
          2000
        )

        // On mobile a plain confirmation shrinks to a tick + title pill. It is
        // the same information a full-width card was spending three lines on,
        // and it stops success chatter from covering the page it refers to.
        //
        // Deliberately NOT applied when the toast carries an action or is
        // destructive: an Undo button is the only way back from a bulk delete,
        // and an error's text IS the payload. Collapsing either to a green tick
        // would drop the part the user actually needs.
        const compact = isMobile && !action && props.variant !== 'destructive'

        if (compact) {
          return (
            <Toast
              key={id}
              duration={autoDuration}
              onClick={() => dismiss(id)}
              className="cursor-pointer gap-2 space-x-0 rounded-full py-2 pl-3 pr-4"
              {...props}
            >
              <Check className="h-4 w-4 shrink-0 text-emerald-500" aria-hidden="true" />
              {/* Title only — the description is the sentence the pill exists to
                  replace. Kept in the a11y tree so screen readers still get it. */}
              {title && <ToastTitle className="truncate">{title}</ToastTitle>}
              {/* `truncate` above needs the `min-w-0` ToastTitle now carries — without
                  it the pill grew to the width of the longest word instead of
                  ellipsising, and slid off the screen edge (IN-04). */}
              {description && <ToastDescription className="sr-only">{description}</ToastDescription>}
            </Toast>
          )
        }

        return (
          <Toast
            key={id}
            duration={autoDuration}
            onClick={() => dismiss(id)}
            className={props.variant !== 'destructive' ? 'cursor-pointer' : undefined}
            {...props}
          >
            {/* `min-w-0`: same flex-item rule as the pill. Without it this column
                sizes to its longest word and the card overflows its viewport. */}
            <div className="grid min-w-0 gap-1">
              {title && <ToastTitle>{title}</ToastTitle>}
              {description && (
                <ToastDescription>{description}</ToastDescription>
              )}
            </div>
            {action}
            {/* Icon-only control: without a name every toast announces an anonymous button (F2). */}
            <ToastClose aria-label={t('button.close')} onClick={e => e.stopPropagation()} />
          </Toast>
        )
      })}
      {/* pt-safe-top keeps toasts below the iOS Dynamic Island / notch on PWA */}
      <ToastViewport className="[padding-top:max(1rem,env(safe-area-inset-top))]" />
    </ToastProvider>
  )
}
